import React, { useEffect, useRef, useState } from "react";
import { Typography, Chip, Box, Paper, Divider, Grid } from "@mui/material";
import { Code as CodeIcon } from "@mui/icons-material";

const skillGroups = [
  {
    category: "Languages",
    color: "#667eea",
    emoji: "💻",
    items: ["Java", "JavaScript", "Python", "C#", "Dart", "PHP", "C"],
  },
  {
    category: "Web & Frameworks",
    color: "#764ba2",
    emoji: "🌐",
    items: [
      "ASP.NET Core",
      "Blazor WebAssembly",
      "React",
      "React Native",
      "Node.js",
      "FastAPI",
      "Flask",
      "HTML",
      "CSS",
      "Tailwind CSS",
    ],
  },
  {
    category: "Cloud & DevOps",
    color: "#06b6d4",
    emoji: "☁️",
    items: [
      "Microsoft Azure",
      "Azure DevOps",
      "Azure Functions",
      "Docker",
      "GitHub Actions (CI/CD)",
      "Firebase",
      "GCP",
      "Nginx",
    ],
  },
  {
    category: "Databases",
    color: "#ec4899",
    emoji: "🗄️",
    items: [
      "MS SQL",
      "MySQL",
      "SQLite",
      "Dapper ORM",
      "Entity Framework",
      "Elastic Search",
    ],
  },
  {
    category: "AI & Machine Learning",
    color: "#f59e0b",
    emoji: "🤖",
    items: [
      "YOLOv8",
      "SAM",
      "SAHI",
      "Scikit-learn",
      "TensorFlow",
      "NumPy",
      "Pandas",
      "Streamlit",
      "Ollama",
      "Mistral AI",
    ],
  },
  {
    category: "Tools & Methodologies",
    color: "#10b981",
    emoji: "🛠️",
    items: [
      "Agile / Scrum",
      "Jira",
      "ServiceNow",
      "SDLC",
      "TDD / xUnit",
      "REST API",
      "Figma",
      "Oxygen XML",
      "Postman",
    ],
  },
];

export const SkillsSection = () => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.05 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <Paper
      ref={ref}
      elevation={0}
      sx={{
        p: { xs: 3, md: 4 },
        mb: 4,
        borderRadius: 4,
        border: "1px solid var(--border-color)",
        background: "var(--card-bg)",
        boxShadow: "0 8px 32px rgba(102,126,234,0.08)",
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(24px)",
        transition: "opacity 0.6s ease-out, transform 0.6s ease-out",
      }}
    >
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: "50%",
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mr: 2,
            flexShrink: 0,
            boxShadow: "0 4px 12px rgba(102,126,234,0.3)",
          }}
        >
          <CodeIcon sx={{ color: "white", fontSize: 21 }} />
        </Box>
        <Typography
          variant="h5"
          fontWeight="bold"
          sx={{ color: "var(--body_color)" }}
        >
          Technical Skills
        </Typography>
      </Box>
      <Divider sx={{ mb: 3 }} />

      <Grid container spacing={3}>
        {skillGroups.map((group, i) => (
          <Grid item xs={12} sm={6} md={4} key={i}>
            <Box
              sx={{
                p: 2,
                borderRadius: 3,
                border: "1px solid var(--border-color)",
                height: "100%",
                transition: "all 0.3s ease",
                "&:hover": {
                  borderColor: group.color + "50",
                  boxShadow: `0 6px 20px ${group.color}18`,
                  transform: "translateY(-3px)",
                },
              }}
            >
              {/* Category header */}
              <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
                <Typography sx={{ fontSize: "1.1rem", mr: 0.8 }}>
                  {group.emoji}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    color: group.color,
                    textTransform: "uppercase",
                    letterSpacing: 0.8,
                    fontSize: "0.72rem",
                  }}
                >
                  {group.category}
                </Typography>
              </Box>

              {/* Skill chips */}
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.7 }}>
                {group.items.map((skill, j) => (
                  <Chip
                    key={j}
                    label={skill}
                    size="small"
                    sx={{
                      fontSize: "0.72rem",
                      height: 24,
                      bgcolor: group.color + "12",
                      color: "var(--body_color)",
                      border: `1px solid ${group.color}30`,
                      fontWeight: 500,
                      transition: "all 0.2s ease",
                      "&:hover": {
                        bgcolor: group.color + "22",
                        transform: "translateY(-2px)",
                        boxShadow: `0 4px 8px ${group.color}25`,
                        color: group.color,
                      },
                    }}
                  />
                ))}
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Paper>
  );
};
